import { useEffect, useMemo, useState, useCallback } from "react";
import PageScaffold from "../../components/PageScaffold";
import GaraazOrderComponent from "./components/GaraazOrderComponent";
import GaraazEventNotification from "./components/GaraazEventNotification";
import {
  Box,
  Flex,
  IconButton,
  Spinner,
  VStack,
  useDisclosure,
  Text,
  HStack,
  Center,
  Link,
  Badge,
  Divider,
  Collapse,
} from "@chakra-ui/react";
import { ChatInbox } from "../Chat/components/ChatInbox";
import ChatBox from "../Chat/components/ChatBox";
import { useChatContext } from "../../context/chatContext";
import { ChatType } from "../../types/chatTypes";
import {
  createChatInboxConfig,
  createChatBoxConfig,
  POSITIONING_CONFIGS,
} from "../../utils/chatConfigCreators";
import { createUnifiedChatHandler } from "../Chat/utils/chatHandlers";
import { useUserDetailsContext } from "../../context/userDetailsContext";
import { TextLabels } from "./enums";
import { useGetOrderMediaConfig } from "../PlaceOrderTest/queryHooks";
import {
  useGetAllGaraazOrders,
  useGetAllOrderHandlers,
  useGetOrderHandlers,
  useGetOrderStatsByOrderHandler,
} from "./queryHooks";
import SideFilterTab from "./components/SideFilterTab";

import { BsFillGearFill } from "react-icons/bs";
import TuneIcon from "@mui/icons-material/Tune";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import SideSettingsTab from "./components/SideSettingsTab";
import { useOrdersStore } from "./store/useOrdersStore";
import useFiltersStore from "./store/useFiltersStore";
import PerformanceCards from "./components/PerformanceCards";
import { Navigation, Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

import "swiper/css/navigation";
import "./index.css";
import { NavLink, useLocation, useParams } from "react-router-dom";
import { dateTodayApiFormat } from "../../utils/dateResolvers";
import useMatricsStore from "./store/useMetricsStore";
import paths from "../../utils/paths";
import { Inbox } from "@suprsend/react";
import { BellIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { useFeedClient } from "@suprsend/react";

export interface OrderHandler {
  _id: string;
  firstName: string;
  lastName: string;
}

export type OrderHandlers = OrderHandler[];

const Index = () => {
  const { pathname } = useLocation();
  const { orderNo: routeOrderNo } = useParams();
  const hideSummarySections = Boolean(routeOrderNo?.trim());
  const filterObject = useFiltersStore((state) => state.filterObject);
  const queryOffset = useOrdersStore((state) => state.queryOffset);
  const setQueryOffset = useOrdersStore((state) => state.setQueryOffset);
  const updateOrderHandlers = useFiltersStore(
    (state) => state.updateOrderHandlers,
  );

  const userDetails = useUserDetailsContext();
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const isCustomerChatAllowed =
    mobileConfigs?.is_customer_chat_allowed_on_orca === "true" ||
    mobileConfigs?.is_customer_chat_allowed_on_orca === true;

  const {
    openSupplierChatData,
    openSupplierChatUniqueName,
    setOpenSupplierChatUniqueName,
    setOpenSupplierChatData,
    openCustomerChatData,
    openCustomerChatUniqueName,
    setOpenCustomerChatUniqueName,
    setOpenCustomerChatData,
  } = useChatContext();

  const { data: allOrderHandlers } = useGetAllOrderHandlers();
  const feedClient = useFeedClient();
  const [isOpen, setIsOpen] = useState(false);

  /** Notification / deep link: fetch by orderNo via v2 API only; do not persist to ORDER_FILTER_SETTINGS. */
  const filterString = useMemo(() => {
    const fromRoute = routeOrderNo?.trim();
    const effectiveOffset = fromRoute ? 0 : queryOffset;
    if (fromRoute) {
      return `&orderNo=${encodeURIComponent(fromRoute)}&limit=${filterObject?.limit}&offset=${effectiveOffset}&includeDeleted=true`;
    }
    if (filterObject.filterOrderNoCheckBoxValue === true) {
      return `&orderNo=${encodeURIComponent(filterObject.filterOrderNo ?? "")}&limit=${filterObject?.limit}&offset=${effectiveOffset}&includeDeleted=true`;
    }
    const includeDeletedParam =
      filterObject.voidedStatus === "VOIDED" ? "&includeDeleted=true" : "";
    return `&limit=${
      filterObject?.limit
    }&offset=${effectiveOffset}&customerEngagementMode=${
      filterObject?.customerType === "customerSearch"
        ? ""
        : filterObject.customerType
    }${filterObject?.orderStatus}${filterObject.supplierOrderStatus}${
      filterObject?.orderHandler
    }&startDate=${
      filterObject.startDate ?? dateTodayApiFormat
    }&endDate=${filterObject.endDate ?? dateTodayApiFormat}${
      filterObject?.customerId
        ? `&customerId=${filterObject.customerId}`
        : ""
    }${filterObject.orderType}${filterObject.transactionType}${
      filterObject.invoiceStatus
    }${
      filterObject.isSupplierInvoicePresent === false
        ? "&isSupplierInvoicePresent=false"
        : ""
    }${filterObject.states}${filterObject.deliveryMode}${
      filterObject.pendingOrderItems ?? ""
    }${includeDeletedParam}`;
  }, [routeOrderNo, filterObject, queryOffset]);

  useEffect(() => {
    if (!routeOrderNo?.trim() || queryOffset === 0) return;
    setQueryOffset(0);
  }, [routeOrderNo, queryOffset, setQueryOffset]);

  const {
    isLoading: loadingOrders,
    isFetched,
    refetch,
  } = useGetAllGaraazOrders(filterString);
  const { data } = useMatricsStore((state: any) => ({ data: state.data }));
  const allorders = useOrdersStore((state) => state.allOrders);
  const isExplicitOrderLookup =
    Boolean(routeOrderNo?.trim()) || filterObject.filterOrderNoCheckBoxValue;
  const showVoidedOrders = filterObject.voidedStatus === "VOIDED";
  const displayedOrders = useMemo(() => {
    if (!allorders?.orders) return [];
    if (isExplicitOrderLookup || showVoidedOrders) {
      return allorders.orders;
    }
    return allorders.orders.filter((order: any) => order.isDeleted !== true);
  }, [allorders?.orders, showVoidedOrders, isExplicitOrderLookup]);

  const scrollOrderId = useOrdersStore((state) => state.scrollOrderId);

  const { data: orderCancellationReason, isLoading: loadingOrderCancellation } =
    useGetOrderMediaConfig();
  const { isLoading: loadingOrderHandlers } = useGetOrderHandlers();
  const { data: orderStats, isLoading: loadingOrderStats } =
    useGetOrderStatsByOrderHandler();
  const orderHandlers = useOrdersStore((state) => state.orderHandlers);

  const {
    isOpen: isSideFilterTabOpen,
    onOpen: onSideFilterTabOpen,
    onClose: onSideFilterTabClose,
  } = useDisclosure();
  const {
    isOpen: isSideSettingsTabOpen,
    onOpen: onSideSettingsTabOpen,
    onClose: onSideSettingsTabClose,
  } = useDisclosure();

  const [orderStartIndex, setOrderStartIndex] = useState(queryOffset + 1);
  const [orderEndIndex, setOrderEndIndex] = useState(
    queryOffset + filterObject.limit > allorders.count
      ? allorders.count
      : queryOffset + filterObject.limit,
  );
  const voidedHiddenOnPage =
    isExplicitOrderLookup || showVoidedOrders
      ? 0
      : (allorders?.orders?.length ?? 0) - displayedOrders.length;
  const visibleOrdersCount = Math.max(
    (allorders?.count ?? 0) - voidedHiddenOnPage,
    displayedOrders.length,
  );
  const visibleOrderEndIndex =
    displayedOrders.length === 0
      ? 0
      : Math.min(orderStartIndex + displayedOrders.length - 1, visibleOrdersCount);
  const [isFiltersChanged, setIsFiltersChanged] = useState(false);
  const topThreeCounts = useMemo(() => {
    const uniqueCounts = Array.from(
      new Set(orderStats?.map((stat: any) => stat.orders_handled_today)),
    );
    const topThree = uniqueCounts.slice(0, 3);
    return topThree;
  }, [orderStats]);

  useEffect(() => {
    if (
      !filterObject?.orderHandler &&
      allOrderHandlers &&
      Array.isArray(allOrderHandlers)
    ) {
      const handlerIds = allOrderHandlers.map(
        (item: { _id: string }) => item._id,
      );
      updateOrderHandlers(handlerIds);
    }
  }, [allOrderHandlers, updateOrderHandlers]);

  useEffect(() => {
    setOrderEndIndex(
      queryOffset + filterObject.limit > allorders.count
        ? allorders.count
        : queryOffset + filterObject.limit,
    );
  }, [allorders.count]);

  useEffect(() => {
    if (isFiltersChanged || queryOffset === 0) {
      setOrderEndIndex(
        allorders?.count >= filterObject.limit
          ? filterObject.limit
          : allorders?.count,
      );
    }
  }, [allorders, isFetched]);

  useEffect(() => {
    refetch();
  }, [queryOffset]);

  useEffect(() => {
    refetch();
  }, [filterString, filterObject]);

  useEffect(() => {
    if (scrollOrderId && (pathname === "/" || pathname.startsWith("/orders/"))) {
      const orderElement = document.getElementById(`${scrollOrderId}`);
      if (orderElement) {
        orderElement.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [pathname]);

  // Chat inbox now uses conversation-based subscription instead of order-based subscription

  const refetchAllOrders = useCallback(() => {
    refetch();
  }, [refetch]);

  async function handleNextOrders() {
    setIsFiltersChanged(false);
    setQueryOffset(orderEndIndex);
    setOrderStartIndex(orderStartIndex + filterObject.limit);
    setOrderEndIndex(
      orderEndIndex + filterObject.limit <= allorders?.count
        ? orderEndIndex + filterObject.limit
        : allorders?.count,
    );
  }

  async function handlePreviousOrders() {
    setIsFiltersChanged(false);
    const newOffset = orderStartIndex - filterObject.limit - 1;
    setQueryOffset(newOffset);
    setOrderEndIndex(orderStartIndex - 1);
    setOrderStartIndex(orderStartIndex - filterObject.limit);
  }

  if (loadingOrders || loadingOrderCancellation || loadingOrderHandlers) {
    return (
      <Flex justifyContent="center" alignItems="center" minH="100vh">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  return (
    <PageScaffold
      title={TextLabels.ORDERS}
      isHeaderVisible={!hideSummarySections}
      headerElements={
        hideSummarySections ? null :
        <Box
          display={"flex"}
          marginTop={"auto"}
          marginBottom={"auto "}
          height={"inherit"}
          zIndex={3}
          alignItems={"center"}
        >
          {feedClient && (
            <Inbox
              popperPosition="bottom"
              bellComponent={() => (
                <IconButton
                  icon={<BellIcon boxSize="22px" color="black" />}
                  aria-label="Notifications"
                  bg="none"
                  color="black"
                  mb={3}
                  ml={2}
                  _hover={{ bgColor: "rgb(225,228,231)" }}
                  _focus={{ bgColor: "white", boxShadow: "none" }}
                />
              )}
              theme={{
                badge: {
                  padding: "2px 3px",
                  position: "absolute",
                  top: "4px",
                  right: "2px",
                  zIndex: 10,
                  fontWeight: "800",
                },
              }}
            />
          )}

          <Box
            width={"0.5px"}
            backgroundColor={"lightgrey"}
            mr={2}
            padding={"2px 0px"}
            boxSizing="border-box"
            height={"70%"}
          ></Box>

          <IconButton
            onClick={onSideFilterTabOpen}
            icon={<TuneIcon />}
            aria-label=""
            bg={"none"}
            color={"black"}
            _hover={{ bgColor: "rgb(225,228,231)" }}
            _focus={{ bgColor: "white", boxShadow: "none" }}
          />

          <Box
            width={"0.5px"}
            backgroundColor={"lightgrey"}
            mr={2}
            ml={2}
            padding={"2px 0px"}
            boxSizing="border-box"
            height={"70%"}
          ></Box>

          <IconButton
            onClick={onSideSettingsTabOpen}
            icon={<BsFillGearFill />}
            aria-label=""
            fontSize={"lg"}
            bg={"none"}
            color={"black"}
            mr={2}
            _hover={{ bgColor: "rgb(225,228,231)" }}
            _focus={{ bgColor: "none", boxShadow: "none" }}
          />

          <Box display={"flex"} alignItems={"center"}>
            <Text
              textAlign={"center"}
              color={"black"}
              fontSize={"small"}
              mr={5}
            >
              Orders {visibleOrdersCount ? orderStartIndex : 0} -{" "}
              {visibleOrderEndIndex} of {visibleOrdersCount}
            </Text>
            <IconButton
              aria-label=""
              icon={<ChevronLeft />}
              onClick={handlePreviousOrders}
              isDisabled={!(orderStartIndex > 1)}
              mr={1}
              bg={"none"}
              color={"black"}
              _hover={{ bgColor: "rgb(225,228,231)" }}
              _disabled={{
                color: "lightgrey",
                bg: "none",
                _hover: { bg: "none", boxShadow: "none" },
              }}
              _focus={{
                bgColor: "none",
                background: "none",
                boxShadow: "none",
              }}
            ></IconButton>
            <IconButton
              aria-label=""
              icon={<ChevronRight />}
              onClick={handleNextOrders}
              isDisabled={!(orderEndIndex < allorders?.count)}
              bg={"none"}
              color={"black"}
              _hover={{ bgColor: "rgb(225,228,231)" }}
              _disabled={{
                color: "lightgrey",
                bg: "none",
                _hover: { bg: "none", boxShadow: "none" },
              }}
              _focus={{
                bgColor: "none",
                background: "none",
                boxShadow: "none",
              }}
            ></IconButton>
          </Box>
        </Box>
      }
    >
      {!hideSummarySections && (
        <Box bg={"white"}>
          <Flex
            w={"100%"}
            h={"120px"}
            borderRadius={"3px"}
            alignItems={"center"}
            bg={"rgb(247, 247, 249)"}
            p={"5px"}
            boxShadow={
              "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px"
            }
          >
            <HStack
              h={"100px"}
              justifyContent="center"
              m={"auto"}
              alignItems={"center"}
              p={"5px"}
              flex="1"
            >
              <Link
                as={NavLink}
                to={paths.customers}
                _hover={{ color: "black" }}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Box
                  w={"250px"}
                  h={"95%"}
                  boxShadow="rgba(0, 0, 0, 0.16) 0px 3px 6px, rgba(0, 0, 0, 0.23) 0px 3px 6px"
                  borderRadius={"4px"}
                  p={"5px"}
                  transition="transform 200ms ease-out"
                  _hover={{ transform: "scale(1.02)" }}
                >
                  <HStack color={"black"}>
                    <Center
                      w={"50%"}
                      h={"100%"}
                      fontSize={"11px"}
                      fontWeight={"bold"}
                      color={"#6CA7FC"}
                    >
                      Today
                    </Center>
                    <Center
                      w={"50%"}
                      h={"100%"}
                      fontSize={"11px"}
                      fontWeight={"bold"}
                      color={"#6CA7FC"}
                    >
                      Yesterday
                    </Center>
                    <Center
                      w={"50%"}
                      h={"100%"}
                      fontSize={"11px"}
                      fontWeight={"bold"}
                      color={"#6CA7FC"}
                    >
                      Month
                    </Center>
                  </HStack>
                  <HStack>
                    <Center
                      w={"50%"}
                      h={"100%"}
                      fontSize={"25px"}
                      fontWeight={"bold"}
                    >
                      {data?.today}
                    </Center>
                    <Center
                      w={"50%"}
                      h={"100%"}
                      fontSize={"25px"}
                      fontWeight={"bold"}
                    >
                      {data?.uniqueCustomersYesterday}
                    </Center>
                    <Center
                      w={"50%"}
                      h={"100%"}
                      fontSize={"25px"}
                      fontWeight={"bold"}
                    >
                      {data?.uniqueCustomersThisMonth}
                    </Center>
                  </HStack>
                  <Text textAlign={"center"} mt={"0px"} fontSize={"12px"}>
                    Unique Customers
                  </Text>
                </Box>
              </Link>
            </HStack>
            <Box>
              <GaraazEventNotification />
            </Box>

            <Box flex="1"></Box>
          </Flex>
          <Box
            mt="10px"
            rounded="xl"
            boxShadow="lg"
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            overflow="hidden"
          >
            <Flex
              align="center"
              justify="space-between"
              bg="gray.50"
              px={4}
              py={3}
              cursor="pointer"
              onClick={() => setIsOpen(!isOpen)}
            >
              <Flex align="center" gap={2}>
                <Text fontWeight="bold" color="gray.700" fontSize="lg">
                  🏆 Order Handler Metrics
                </Text>
                <Badge colorScheme="blue" fontSize="0.8em">
                  {orderStats?.length || 0}
                </Badge>
              </Flex>

              <Flex align="center" gap={1}>
                <Text fontSize="sm" color="gray.500">
                  {isOpen ? "Hide" : "Show"}
                </Text>
                <IconButton
                  aria-label="Toggle"
                  icon={
                    <ChevronDownIcon
                      transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
                      transition="transform 0.2s ease"
                    />
                  }
                  size="sm"
                  variant="ghost"
                />
              </Flex>
            </Flex>
            <Divider />
            <Collapse in={isOpen} animateOpacity>
              <Swiper
                modules={[Navigation, Autoplay]}
                navigation
                autoplay={{
                  delay: 2500,
                  disableOnInteraction: false,
                }}
                slidesPerView={Math.floor(window.innerWidth / 250)}
                spaceBetween={10}
              >
                {orderStats?.map((orderHandler: any, index: number) => (
                  <SwiperSlide key={index}>
                    <PerformanceCards
                      userData={orderHandler}
                      index={
                        orderHandler.orders_handled_today === topThreeCounts[0]
                          ? 0
                          : orderHandler.orders_handled_today ===
                              topThreeCounts[1]
                            ? 1
                            : orderHandler.orders_handled_today ===
                                topThreeCounts[2]
                              ? 2
                              : -1
                      }
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </Collapse>
          </Box>
        </Box>
      )}
      <VStack height={"100%"}>
        {displayedOrders.map((order: any, index: number) => (
          <GaraazOrderComponent
            refetchAllOrders={refetchAllOrders}
            key={order.orderNo}
            orderData={order}
            orderHandlers={orderHandlers}
            orderCancellationReason={JSON.parse(
              orderCancellationReason?.order_cancellation_reasons,
            )}
            filterString={filterString}
          />
        ))}

        {displayedOrders.length === 0 && (
          <Box mt={40}>
            <Text>No orders For selected Filters</Text>
          </Box>
        )}
      </VStack>

      <SideFilterTab
        isOpen={isSideFilterTabOpen}
        onClose={onSideFilterTabClose}
        setOrderStartIndex={setOrderStartIndex}
        setIsFiltersChanged={setIsFiltersChanged}
        refetchAllOrders={refetchAllOrders}
      />

      <SideSettingsTab
        isOpen={isSideSettingsTabOpen}
        onClose={onSideSettingsTabClose}
        setIsFiltersChanged={setIsFiltersChanged}
        setOrderStartIndex={setOrderStartIndex}
        refetchAllOrders={refetchAllOrders}
      />

      {(() => {
        const chatHandler = createUnifiedChatHandler({
          setOpenSupplierChatData,
          setOpenSupplierChatUniqueName,
          setOpenCustomerChatData,
          setOpenCustomerChatUniqueName,
          userDetails,
        });

        const supplierInboxConfig = createChatInboxConfig(
          ChatType.SUPPLIER,
          "right",
          chatHandler,
        );

        const customerInboxConfig = createChatInboxConfig(
          ChatType.CUSTOMER,
          "left",
          chatHandler,
        );

        const supplierChatBoxConfig = createChatBoxConfig(
          ChatType.SUPPLIER,
          POSITIONING_CONFIGS.supplier,
        );

        const customerChatBoxConfig = createChatBoxConfig(
          ChatType.CUSTOMER,
          POSITIONING_CONFIGS.customer,
        );

        return (
          <>
            <ChatInbox config={supplierInboxConfig} />
            {isCustomerChatAllowed && (
              <ChatInbox config={customerInboxConfig} />
            )}

            {openSupplierChatData && openSupplierChatData.participantId && (
              <ChatBox
                key={openSupplierChatUniqueName}
                config={supplierChatBoxConfig}
                orderNumber={openSupplierChatData.orderNumber}
                participantName={openSupplierChatData.participantName}
                orderHandlerUserId={openSupplierChatData.orderHandlerUserId}
                participantId={openSupplierChatData.participantId}
                onClose={() => {
                  setOpenSupplierChatUniqueName(null);
                  setOpenSupplierChatData(null);
                }}
              />
            )}

            {isCustomerChatAllowed &&
              openCustomerChatData &&
              openCustomerChatData.participantId && (
                <ChatBox
                  key={openCustomerChatUniqueName}
                  config={customerChatBoxConfig}
                  orderNumber={openCustomerChatData.orderNumber}
                  participantName={openCustomerChatData.participantName}
                  orderHandlerUserId={openCustomerChatData.orderHandlerUserId}
                  participantId={openCustomerChatData.participantId}
                  onClose={() => {
                    setOpenCustomerChatUniqueName(null);
                    setOpenCustomerChatData(null);
                  }}
                />
              )}
          </>
        );
      })()}
    </PageScaffold>
  );
};

export default Index;
